/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/promise-function-async */
import type {AppRouter} from '@core/server/app_router';
import {__APP_ENV__} from '@src/app_env';
import {
   createTRPCClient,
   createWSClient,
   httpLink,
   loggerLink,
   splitLink,
   TRPCClientError,
   wsLink,
} from '@trpc/client';
import type {inferRouterInputs, inferRouterOutputs} from '@trpc/server';

export type TRPCClientType = ReturnType<typeof createTRPCClient<AppRouter>>;
export type RouterOutput = inferRouterOutputs<AppRouter>;
export type RouterInput = inferRouterInputs<AppRouter>;

/**
 * Type guard to check if an error is a TRPCClientError.
 * Use this to safely access tRPC error properties like `data.code` and `message`.
 */
export function isTRPCClientError(err: unknown): err is TRPCClientError<AppRouter> {
   return err instanceof TRPCClientError;
}

// Singleton instances
let _trpcClient: TRPCClientType | null = null;
let _wsClient: ReturnType<typeof createWSClient> | null = null;

let extra_headers_provider: (() => Record<string, string>) | null = null;

/**
 * Register a provider whose headers are added to every tRPC HTTP request. Lets an app
 * attach per-request context (the main app sends its analytics session ids) without
 * this shared client importing app code.
 */
export function setTrpcExtraHeaders(provider: () => Record<string, string>): void {
   extra_headers_provider = provider;
}

/**
 * Return the singleton WebSocket client instance.
 * Connects directly to the backend's /trpc_ws endpoint.
 * Cookies (session auth) are sent automatically on the WS upgrade handshake.
 */
function getWsClient(): ReturnType<typeof createWSClient> {
   if (_wsClient != null) {
      return _wsClient;
   }

   const ws_url = `${__APP_ENV__.WS_URL as string}/trpc_ws`;

   console.log(`WebSocket: Initializing WS client — url: ${ws_url}`);

   _wsClient = createWSClient({
      url: ws_url,
      keepAlive: {
         enabled: true,
         intervalMs: 7_000,
         pongTimeoutMs: 2_000,
      },
      onOpen: () => {
         console.log(`WebSocket: Connected to ${ws_url}`);
      },
      onClose: (cause) => {
         console.log(`WebSocket: Disconnected from ${ws_url}`, cause);
      },
   });

   return _wsClient;
}

/**
 * Close the WebSocket client and drop the tRPC client so the next call
 * reconnects with the current session cookie. Call after sign-in, sign-out
 * or any other session change.
 */
export async function resetTrpcClient(): Promise<void> {
   if (_wsClient != null) {
      console.log('WebSocket: Closing connection for re-authentication');
      await _wsClient.close();
      _wsClient = null;
   }
   // The trpc client holds a reference to the old wsLink, so it must be rebuilt too
   _trpcClient = null;
}

/**
 * Return the singleton instance of the trpc client.
 */
export function trpcClient(): ReturnType<typeof createTRPCClient<AppRouter>> {
   if (_trpcClient != null) {
      return _trpcClient;
   }

   _trpcClient = createTRPCClient<AppRouter>({
      links: [
         loggerLink({
            enabled: () => true,
         }),
         splitLink({
            condition: (op) => op.type === 'subscription',
            true: wsLink({
               client: getWsClient(),
            }),
            false: httpLink({
               url: `${__APP_ENV__.API_URL as string}/trpc`,
               // Route everything over POST so the params are not in the URL.
               methodOverride: 'POST',
               fetch(url: any, options: any) {
                  return fetch(url, {
                     ...options,
                     credentials: 'include',
                     headers: {...options?.headers, ...extra_headers_provider?.()},
                  });
               },
            }),
         }),
      ],
   });

   return _trpcClient;
}
